export const WORKFLOW_TYPES = {
    SCAN_PROCESS: 'SCAN_PROCESS',
    CLINICAL_MODIFICATION: 'CLINICAL_MODIFICATION',
    ALIGNMENT_AND_LOCKING: 'ALIGNMENT_AND_LOCKING',
    FINALIZATION_PROCESS: 'FINALIZATION_PROCESS',
    /** @deprecated Use CLINICAL_MODIFICATION, ALIGNMENT_AND_LOCKING, or FINALIZATION_PROCESS */
    MAIN_PROCESS: 'MAIN_PROCESS',
};

export const SCAN_PROCESS_STEPS = {
    PREPARING_SCAN: 'PREPARING_SCAN',
    AXES_ALIGNMENT: 'AXES_ALIGNMENT',
    /** @deprecated Preview screen removed — kept only so legacy references resolve. */
    FINAL_DESIGN_PREVIEW: 'FINAL_DESIGN_PREVIEW',
};

/** Navigable scan steps (preview screen removed from the flow). */
export const SCAN_PROCESS_FLOW_STEPS = [
    SCAN_PROCESS_STEPS.PREPARING_SCAN,
    SCAN_PROCESS_STEPS.AXES_ALIGNMENT,
];

export const MAIN_PROCESS_STEPS = {
    RECTIFICATION: 'RECTIFICATION',
    ELONGATION: 'ELONGATION',
    TRIMLINE: 'TRIMLINE',
    FLARES: 'FLARES',
    ML_REDUCTION: 'ML_REDUCTION',
    THICKNESS: 'THICKNESS',
    CLINICAL_MODIFICATION_PREVIEW: 'CLINICAL_MODIFICATION_PREVIEW',
    LOCKING_MECHANISM: 'LOCKING_MECHANISM',
    VALVE: 'VALVE',
    LOGO_PLACEMENT: 'LOGO_PLACEMENT',
    PADS: 'PADS',
    VALVE_PATTERN: 'VALVE_PATTERN',
    FINALIZATION: 'FINALIZATION',
};

export const CLINICAL_MODIFICATION_STEPS = [
    MAIN_PROCESS_STEPS.RECTIFICATION,
    MAIN_PROCESS_STEPS.ELONGATION,
    MAIN_PROCESS_STEPS.TRIMLINE,
    MAIN_PROCESS_STEPS.ML_REDUCTION,
    MAIN_PROCESS_STEPS.FLARES,
    MAIN_PROCESS_STEPS.THICKNESS,
];

/** Steps where the socket uses neutral gray when vertex colors are absent (e.g. after locking CSG). */
export const ALIGNMENT_AND_LOCKING_STEPS = [
    MAIN_PROCESS_STEPS.LOCKING_MECHANISM,
    MAIN_PROCESS_STEPS.VALVE,
    MAIN_PROCESS_STEPS.LOGO_PLACEMENT,
];

export function isAlignmentLockingSocketStep(step) {
    return ALIGNMENT_AND_LOCKING_STEPS.includes(step);
}

export const DESIGN_ROUTE_PREFIX = {
    CLINICAL_MODIFICATION: '/design/clinical-modification',
    ALIGNMENT_AND_LOCKING: '/design/alignment-and-locking',
    FINALIZATION: '/design/finalization',
};

export const PATHS = {
    [SCAN_PROCESS_STEPS.PREPARING_SCAN]: '/design/scan/preparing',
    [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: '/design/scan/axes-alignment',
    [SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW]: '/design/scan/final-preview',

    [MAIN_PROCESS_STEPS.RECTIFICATION]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/rectification`,
    [MAIN_PROCESS_STEPS.ELONGATION]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/elongation`,
    [MAIN_PROCESS_STEPS.TRIMLINE]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/trimline`,
    [MAIN_PROCESS_STEPS.ML_REDUCTION]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/ml-reduction`,
    [MAIN_PROCESS_STEPS.FLARES]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/flares`,
    [MAIN_PROCESS_STEPS.THICKNESS]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/thickness`,
    [MAIN_PROCESS_STEPS.CLINICAL_MODIFICATION_PREVIEW]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/preview`,
    [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: `${DESIGN_ROUTE_PREFIX.ALIGNMENT_AND_LOCKING}/locking-mechanism`,
    [MAIN_PROCESS_STEPS.VALVE]: `${DESIGN_ROUTE_PREFIX.ALIGNMENT_AND_LOCKING}/valve`,
    [MAIN_PROCESS_STEPS.LOGO_PLACEMENT]: `${DESIGN_ROUTE_PREFIX.ALIGNMENT_AND_LOCKING}/logo-placement`,
    [MAIN_PROCESS_STEPS.PADS]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/pads`,
    [MAIN_PROCESS_STEPS.VALVE_PATTERN]: `${DESIGN_ROUTE_PREFIX.CLINICAL_MODIFICATION}/valve-pattern`,
    [MAIN_PROCESS_STEPS.FINALIZATION]: DESIGN_ROUTE_PREFIX.FINALIZATION,
};

/** @deprecated `/design/main/*` — use PATHS or DESIGN_ROUTE_PREFIX */
export const LEGACY_MAIN_PATH_REDIRECTS = {
    '/design/main/rectification': PATHS[MAIN_PROCESS_STEPS.RECTIFICATION],
    '/design/main/elongation': PATHS[MAIN_PROCESS_STEPS.ELONGATION],
    '/design/main/trimline': PATHS[MAIN_PROCESS_STEPS.TRIMLINE],
    '/design/main/ml-reduction': PATHS[MAIN_PROCESS_STEPS.ML_REDUCTION],
    '/design/main/flares': PATHS[MAIN_PROCESS_STEPS.FLARES],
    '/design/main/thickness': PATHS[MAIN_PROCESS_STEPS.THICKNESS],
    '/design/main/locking-mechanism': PATHS[MAIN_PROCESS_STEPS.LOCKING_MECHANISM],
    '/design/main/alignment': PATHS[MAIN_PROCESS_STEPS.LOCKING_MECHANISM],
    '/design/main/valve': PATHS[MAIN_PROCESS_STEPS.VALVE],
    '/design/main/logo-placement': PATHS[MAIN_PROCESS_STEPS.LOGO_PLACEMENT],
    // '/design/main/pads': PATHS[MAIN_PROCESS_STEPS.PADS],
    '/design/main/valve-pattern': PATHS[MAIN_PROCESS_STEPS.VALVE_PATTERN],
    '/design/main/finalization': PATHS[MAIN_PROCESS_STEPS.FINALIZATION],
};

export function getStepFromPathname(pathname) {
    const entry = Object.entries(PATHS).find(([, path]) => path === pathname);
    return entry?.[0] ?? null;
}

export function getWorkflowStepsForStep(stepId) {
    const workflow = getWorkflowTypeForStep(stepId);
    if (!workflow) return [];
    return WORKFLOW_CONFIG[workflow]?.steps ?? [];
}

export function isFinalizationPath(pathname) {
    return pathname === PATHS[MAIN_PROCESS_STEPS.FINALIZATION];
}

/** Split layout: preview panel left, 3D viewer right (not canvas-embedded step UI). */
export function isPreviewStepPath(pathname) {
    return (
        pathname === PATHS[SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW] ||
        pathname === PATHS[MAIN_PROCESS_STEPS.CLINICAL_MODIFICATION_PREVIEW]
    );
}

export function syncWorkflowFromPathname(pathname) {
    const step = getStepFromPathname(pathname);
    if (!step) return;
    const workflow = getWorkflowTypeForStep(step);
    if (!workflow) return;
    return { workflow, step };
}

/** Global step order for cross-process navigation and overview access rules */
export const GLOBAL_STEP_ORDER = [
    ...SCAN_PROCESS_FLOW_STEPS,
    ...CLINICAL_MODIFICATION_STEPS,
    MAIN_PROCESS_STEPS.LOCKING_MECHANISM,
    MAIN_PROCESS_STEPS.VALVE,
    MAIN_PROCESS_STEPS.LOGO_PLACEMENT,
    MAIN_PROCESS_STEPS.FINALIZATION,
];

export const PROCESS_OVERVIEW_PATH = '/dashboard/processes';

/** Brand mark shown for each design process (breadcrumb, overview, etc.) */
export const PROCESS_LOGO_SRC = '/logo.png';

export const PROCESS_DEFINITIONS = [
    {
        id: WORKFLOW_TYPES.SCAN_PROCESS,
        label: 'Scan Process',
        description: 'Prepare the scan with upload, cleanup, axis alignment, and preview before clinical design begins.',
        steps: SCAN_PROCESS_FLOW_STEPS.map((id) => ({
            id,
            path: PATHS[id],
        })),
    },
    {
        id: WORKFLOW_TYPES.CLINICAL_MODIFICATION,
        label: 'Clinical Modification',
        description: 'Shape the socket with rectification, trimlines, flares, elongation, and controlled wall thickness.',
        steps: CLINICAL_MODIFICATION_STEPS.map((id) => ({
            id,
            path: PATHS[id],
        })),
    },
    {
        id: WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING,
        label: 'Alignment and Locking',
        description: 'Position the socket on the reference ladder, configure the locking mechanism, place the valve, and add the brand logo.',
        steps: [
            {
                id: MAIN_PROCESS_STEPS.LOCKING_MECHANISM,
                path: PATHS[MAIN_PROCESS_STEPS.LOCKING_MECHANISM],
            },
            {
                id: MAIN_PROCESS_STEPS.VALVE,
                path: PATHS[MAIN_PROCESS_STEPS.VALVE],
            },
            {
                id: MAIN_PROCESS_STEPS.LOGO_PLACEMENT,
                path: PATHS[MAIN_PROCESS_STEPS.LOGO_PLACEMENT],
            },
        ],
    },
    {
        id: WORKFLOW_TYPES.FINALIZATION_PROCESS,
        label: 'Finalization',
        description: 'Complete the final design review, export deliverables, and close out the prosthetic order.',
        steps: [
            {
                id: MAIN_PROCESS_STEPS.FINALIZATION,
                path: PATHS[MAIN_PROCESS_STEPS.FINALIZATION],
            },
        ],
    },
];

export function getWorkflowTypeForStep(step) {
    if (Object.values(SCAN_PROCESS_STEPS).includes(step)) return WORKFLOW_TYPES.SCAN_PROCESS;
    if (CLINICAL_MODIFICATION_STEPS.includes(step)) return WORKFLOW_TYPES.CLINICAL_MODIFICATION;
    if (
        step === MAIN_PROCESS_STEPS.LOCKING_MECHANISM ||
        step === MAIN_PROCESS_STEPS.VALVE ||
        step === MAIN_PROCESS_STEPS.LOGO_PLACEMENT
    ) return WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING;
    if (step === MAIN_PROCESS_STEPS.FINALIZATION) return WORKFLOW_TYPES.FINALIZATION_PROCESS;
    return null;
}

export function canAccessStepGlobally(step, stepCompletion) {
    const index = GLOBAL_STEP_ORDER.indexOf(step);
    if (index < 0) return false;
    if (index === 0) return true;
    return GLOBAL_STEP_ORDER.slice(0, index).every((s) => stepCompletion[s]);
}

export function isProcessComplete(processId, stepCompletion) {
    const def = PROCESS_DEFINITIONS.find((p) => p.id === processId);
    if (!def?.steps?.length) return false;
    return def.steps.every((s) => stepCompletion[s.id]);
}

export function getFirstStepForProcess(processId) {
    const def = PROCESS_DEFINITIONS.find((p) => p.id === processId);
    return def?.steps?.[0]?.id ?? null;
}

export const WORKFLOW_CONFIG = {
    [WORKFLOW_TYPES.SCAN_PROCESS]: {
        steps: SCAN_PROCESS_FLOW_STEPS,
        previousWorkflow: null,
        nextWorkflow: {
            workflow: WORKFLOW_TYPES.CLINICAL_MODIFICATION,
            step: MAIN_PROCESS_STEPS.RECTIFICATION,
        },
    },
    [WORKFLOW_TYPES.CLINICAL_MODIFICATION]: {
        steps: CLINICAL_MODIFICATION_STEPS,
        previousWorkflow: {
            workflow: WORKFLOW_TYPES.SCAN_PROCESS,
            step: SCAN_PROCESS_STEPS.AXES_ALIGNMENT,
        },
        nextWorkflow: {
            workflow: WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING,
            step: MAIN_PROCESS_STEPS.LOCKING_MECHANISM,
        },
        stepHandlers: {
            [MAIN_PROCESS_STEPS.FLARES]: {
                onComplete: (set) => {
                    set({
                        selectedSocketType: 'definitive_socket',
                        showSocketTypeModal: false,
                        currentStep: MAIN_PROCESS_STEPS.THICKNESS,
                    });
                },
            },
        },
    },
    [WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING]: {
        steps: [
            MAIN_PROCESS_STEPS.LOCKING_MECHANISM,
            MAIN_PROCESS_STEPS.VALVE,
            MAIN_PROCESS_STEPS.LOGO_PLACEMENT,
        ],
        previousWorkflow: {
            workflow: WORKFLOW_TYPES.CLINICAL_MODIFICATION,
            step: MAIN_PROCESS_STEPS.THICKNESS,
        },
        nextWorkflow: {
            workflow: WORKFLOW_TYPES.FINALIZATION_PROCESS,
            step: MAIN_PROCESS_STEPS.FINALIZATION,
        },
    },
    [WORKFLOW_TYPES.FINALIZATION_PROCESS]: {
        steps: [MAIN_PROCESS_STEPS.FINALIZATION],
        previousWorkflow: {
            workflow: WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING,
            step: MAIN_PROCESS_STEPS.LOGO_PLACEMENT,
        },
        nextWorkflow: null,
    },
};

/** Map legacy persisted workflow values to the new split workflows */
export function normalizeWorkflowType(workflow, step) {
    if (workflow === WORKFLOW_TYPES.MAIN_PROCESS || !workflow) {
        return getWorkflowTypeForStep(step) ?? WORKFLOW_TYPES.CLINICAL_MODIFICATION;
    }
    if (DESIGN_WORKFLOW_TYPES.includes(workflow)) {
        return workflow;
    }
    return getWorkflowTypeForStep(step) ?? workflow;
}

export const DESIGN_WORKFLOW_TYPES = [
    WORKFLOW_TYPES.SCAN_PROCESS,
    WORKFLOW_TYPES.CLINICAL_MODIFICATION,
    WORKFLOW_TYPES.ALIGNMENT_AND_LOCKING,
    WORKFLOW_TYPES.FINALIZATION_PROCESS,
];

export function isActiveDesignWorkflow(workflow) {
    const normalized = normalizeWorkflowType(workflow);
    return DESIGN_WORKFLOW_TYPES.includes(normalized);
}
